/**
 * Document Upload Page
 *
 * Upload institutional documents (guidelines, protocols, procedures, policies).
 * Documents are uploaded to knowledge-service-go, chunked, and added to vector DB.
 * Admin approval required for public documents.
 */

import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, CheckCircle, Loader2, FileUp, Link } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { StatusBadge } from '@/components/documents/StatusBadge';
import {
  createDocument,
  hasInvalidTitleChars,
  pollDocumentStatus,
  type DocumentCategory,
  type DocumentScope,
  type DocumentStatus,
} from '@/api/endpoints/documents';
import { createLogger } from '@/utils/logger';
import { sanitizeErrorMessage } from '@/utils/errorMessages';
import { useKnowledgeCategories } from '@/hooks/useKnowledgeCategories';
import { MAIN_APP_ROUTES } from '@/lib/routes';
// Department scope shares the document with one org-unit/location. The picker
// reuses the same fetcher + query key as the org console and the post-upload
// ShareDialog, so the catalogue is shared and cached across all three surfaces.
import { useAuthenticatedQuery } from '@/hooks/useAuthenticatedQuery';
import { QUERY_STALE_TIMES } from '@/lib/query-config';
import { orgKeys } from '@/components/admin/org/useOrg';
import { orgApi, type Location } from '@/api/endpoints/org';

const logger = createLogger('DocumentUpload');

const ACCEPTED_TYPES = ['.pdf', '.docx', '.txt', '.md'];
const ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/markdown',
];

export default function DocumentUpload() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [mode, setMode] = useState<'file' | 'url'>('file');
  const [file, setFile] = useState<File | null>(null);
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<DocumentCategory>('guideline');
  const [scope, setScope] = useState<DocumentScope>('private');
  // The org-unit/location id selected for 'department' scope (empty = none chosen).
  const [locationId, setLocationId] = useState('');

  const [isUploading, setIsUploading] = useState(false);
  const [uploadedDocId, setUploadedDocId] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<DocumentStatus | null>(null);
  const [chunkCount, setChunkCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // AbortController for polling cleanup
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const configCategories = useKnowledgeCategories();

  // Fall back to a minimal default if config hasn't loaded yet
  const categories = configCategories.length > 0
    ? configCategories
    : [{ value: 'guideline' as DocumentCategory, label: t('documents.upload.categoryFallback', 'Guideline') }];

  // Org units for the 'department' scope picker. Fetched lazily — only once the
  // user selects department scope — and cached under the shared org query key.
  const locationsQuery = useAuthenticatedQuery<Location[]>({
    queryKey: orgKeys.locations(),
    queryFn: () => orgApi.listAllLocations(),
    enabled: scope === 'department',
    // Borrow the `users` staleness bucket (600s): org structure changes about as
    // rarely as the user list and there is no locations-specific clock. Same
    // deliberate choice as ShareDialog's locations query.
    staleTime: QUERY_STALE_TIMES.users,
    refetchOnWindowFocus: false,
  });
  const locations = locationsQuery.data ?? [];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    // Validate file type
    if (!ACCEPTED_MIME_TYPES.includes(selectedFile.type)) {
      setError(t('documents.upload.error.unsupportedType', { types: ACCEPTED_TYPES.join(', ') }));
      return;
    }

    setFile(selectedFile);
    setError(null);

    // Auto-fill title from filename if empty
    if (!title) {
      const filename = selectedFile.name.replace(/\.[^/.]+$/, ''); // Remove extension
      setTitle(filename);
    }
  };

  const handleUpload = async () => {
    if (mode === 'file' && (!file || !title.trim())) {
      setError(t('documents.upload.error.selectFile'));
      return;
    }

    if (mode === 'url') {
      const trimmed = url.trim();
      if (!trimmed) {
        setError(t('documents.upload.error.urlRequired', 'Please enter a URL'));
        return;
      }
      try {
        const parsed = new URL(trimmed);
        if (!parsed.protocol.startsWith('https')) {
          setError(t('documents.upload.error.httpsOnly', 'Only HTTPS URLs are supported'));
          return;
        }
      } catch {
        setError(t('documents.upload.error.invalidUrl', 'Please enter a valid URL (e.g. https://pmc.ncbi.nlm.nih.gov/articles/PMC...)'));
        return;
      }
    }

    // Validate title for XSS-prone characters (file mode only — URL mode resolves title from content)
    if (mode === 'file') {
      if (hasInvalidTitleChars(title.trim())) {
        setError(t('documents.upload.error.invalidTitle'));
        return;
      }
    }

    if (scope === 'department' && !locationId) {
      setError(t('documents.upload.error.departmentRequired'));
      return;
    }

    setIsUploading(true);
    setError(null);
    setUploadedDocId(null);
    setUploadStatus(null);

    try {
      const scopeTarget = scope === 'department' ? locationId : undefined;

      const response = mode === 'url'
        ? await createDocument({
            kind: 'url',
            url: url.trim(),
            category,
            scope,
            scopeTarget,
          })
        : await createDocument({
            kind: 'file',
            file: file!,
            title: title.trim(),
            category,
            scope,
            scopeTarget,
          });

      const { document_id: documentId, status } = response;

      if (mode === 'url') {
        setTitle(response.title || url.trim());
        if (response.chunk_count !== undefined) setChunkCount(response.chunk_count);
      }

      setUploadedDocId(documentId);
      setUploadStatus(status);
      setIsUploading(false);

      if (status === 'processing') {
        abortControllerRef.current = new AbortController();
        pollDocumentStatus(documentId, abortControllerRef.current.signal)
          .then((finalStatus) => {
            abortControllerRef.current = null;
            if (finalStatus) {
              setUploadStatus(finalStatus.status);
              setChunkCount(finalStatus.chunks_processed);
              if (finalStatus.status === 'error') {
                setError(finalStatus.error_message || t('documents.upload.error.uploadFailed'));
              }
            }
          })
          .catch((err: unknown) => {
            abortControllerRef.current = null;
            logger.warn('Status polling failed (upload already succeeded)', {
              documentId,
              error: err,
            });
          });
      }

      logger.info('Upload complete', { documentId, status });
    } catch (err) {
      logger.error('Upload failed', { error: err });
      const msg = err instanceof Error ? err.message : t('documents.upload.error.uploadFailed', 'Upload failed');
      setError(sanitizeErrorMessage(msg));
      setIsUploading(false);
    }
  };

  const handleReset = () => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setFile(null);
    setUrl('');
    setTitle('');
    setCategory(mode === 'url' ? 'article' : 'guideline');
    setScope('private');
    setLocationId('');
    setUploadedDocId(null);
    setUploadStatus(null);
    setChunkCount(0);
    setError(null);
  };

  // 'department' scope requires a chosen location; 'private'/'public' do not.
  const scopeReady = scope !== 'department' || locationId.length > 0;
  const isFormValid = mode === 'url'
    ? url.trim().length > 0 && scopeReady
    : file !== null && title.trim().length > 0 && scopeReady;

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white border-b border-neutral-200">
        <div className="px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(MAIN_APP_ROUTES.documents)}
              aria-label={t('documents.upload.backToList')}
              className="w-9 h-9 -ml-2 flex items-center justify-center text-primary"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h1 className="text-base font-semibold text-neutral-900">{t('documents.upload.title')}</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="p-4 max-w-2xl mx-auto">
        <Card className="p-6">
          {/* Success state */}
          {uploadedDocId && uploadStatus && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-12 h-12 text-green-500" />
                <div>
                  <h2 className="text-lg font-semibold text-neutral-900">
                    {t('documents.upload.success.title')}
                  </h2>
                  <p className="text-sm text-neutral-500">{title}</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-neutral-600">{t('documents.upload.success.statusLabel')}</span>
                <StatusBadge status={uploadStatus} />
              </div>

              {chunkCount > 0 && (
                <p className="text-sm text-neutral-600">
                  {t('documents.upload.success.chunksProcessed', { count: chunkCount })}
                </p>
              )}

              {uploadStatus === 'pending_approval' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
                  {t('documents.upload.success.pendingApprovalNotice')}
                </div>
              )}

              <div className="flex gap-2">
                <Button onClick={handleReset} variant="outline" className="flex-1">
                  {t('documents.upload.success.uploadAnother')}
                </Button>
                <Button onClick={() => navigate(MAIN_APP_ROUTES.documents)} className="flex-1">
                  {t('documents.upload.success.viewDocuments')}
                </Button>
              </div>
            </div>
          )}

          {/* Upload form */}
          {!uploadedDocId && (
            <div className="space-y-6">
              {/* Mode toggle: File or URL */}
              <div className="flex gap-2 p-1 bg-neutral-100 rounded-lg">
                <button
                  type="button"
                  onClick={() => { setMode('file'); setCategory('guideline'); }}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    mode === 'file'
                      ? 'bg-white text-neutral-900 shadow-sm'
                      : 'text-neutral-500 hover:text-neutral-700'
                  }`}
                >
                  <FileUp className="w-4 h-4" />
                  {t('documents.upload.modeFile', 'File')}
                </button>
                <button
                  type="button"
                  onClick={() => { setMode('url'); setCategory('article'); }}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    mode === 'url'
                      ? 'bg-white text-neutral-900 shadow-sm'
                      : 'text-neutral-500 hover:text-neutral-700'
                  }`}
                >
                  <Link className="w-4 h-4" />
                  {t('documents.upload.modeURL', 'URL')}
                </button>
              </div>

              {/* URL input (URL mode) */}
              {mode === 'url' && (
                <div>
                  <label htmlFor="url" className="block text-sm font-medium text-neutral-700 mb-2">
                    {t('documents.upload.urlLabel', 'URL')}
                  </label>
                  <Input
                    id="url"
                    type="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder={t('documents.upload.urlPlaceholder', 'https://pmc.ncbi.nlm.nih.gov/articles/PMC...')}
                    disabled={isUploading}
                  />
                  <p className="mt-1 text-xs text-neutral-500">
                    {t('documents.upload.urlHint', 'PubMed, PMC, or any web page')}
                  </p>
                </div>
              )}

              {/* File picker (File mode) */}
              {mode === 'file' && (
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    {t('documents.upload.fileLabel')}{' '}
                    <span className="text-neutral-400 font-normal">
                      ({t('documents.upload.acceptedTypes')}: {ACCEPTED_TYPES.join(', ')})
                    </span>
                  </label>
                  <Input
                    type="file"
                    accept={ACCEPTED_TYPES.join(',')}
                    onChange={handleFileChange}
                    disabled={isUploading}
                    className="cursor-pointer"
                  />
                  {file && (
                    <p className="mt-2 text-sm text-neutral-500">
                      {t('documents.upload.selected', {
                        name: file.name,
                        size: (file.size / 1024).toFixed(1),
                      })}
                    </p>
                  )}
                </div>
              )}

              {/* Title (File mode only — URL mode resolves title automatically) */}
              {mode === 'file' && (
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-neutral-700 mb-2">
                    {t('documents.upload.titleLabel')}
                  </label>
                  <Input
                    id="title"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder={t('documents.upload.titlePlaceholder')}
                    disabled={isUploading}
                    maxLength={200}
                  />
                </div>
              )}

              {/* Category */}
              <div>
                  <label htmlFor="category" className="block text-sm font-medium text-neutral-700 mb-2">
                    {t('documents.upload.categoryLabel')}
                  </label>
                  <select
                    id="category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value as DocumentCategory)}
                    disabled={isUploading}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {categories.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
              </div>

              {/* Scope */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  {t('documents.upload.scopeLabel')}
                </label>
                <div className="flex flex-col gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="scope"
                      value="private"
                      checked={scope === 'private'}
                      onChange={() => setScope('private')}
                      disabled={isUploading}
                      className="text-primary focus:ring-primary"
                    />
                    <span className="text-sm text-neutral-700">{t('documents.upload.scopePrivate')}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="scope"
                      value="department"
                      checked={scope === 'department'}
                      onChange={() => setScope('department')}
                      disabled={isUploading}
                      className="text-primary focus:ring-primary"
                    />
                    <span className="text-sm text-neutral-700">{t('documents.upload.scopeDepartment')}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="scope"
                      value="public"
                      checked={scope === 'public'}
                      onChange={() => setScope('public')}
                      disabled={isUploading}
                      className="text-primary focus:ring-primary"
                    />
                    <span className="text-sm text-neutral-700">{t('documents.upload.scopePublic')}</span>
                  </label>
                </div>
              </div>

              {/* Location selector (department scope) — all four async states:
                  error (retry), loading, empty, data. */}
              {scope === 'department' && (
                <div>
                  <label htmlFor="department" className="block text-sm font-medium text-neutral-700 mb-2">
                    {t('documents.upload.departmentLabel')}
                  </label>
                  {locationsQuery.isError ? (
                    <button
                      type="button"
                      onClick={() => locationsQuery.refetch()}
                      className="w-full px-3 py-2 border border-red-200 bg-red-50 rounded-lg text-sm text-red-700 text-left"
                    >
                      {t('documents.upload.departmentLoadError')}
                    </button>
                  ) : (
                    <select
                      id="department"
                      value={locationId}
                      onChange={(e) => setLocationId(e.target.value)}
                      disabled={isUploading || locationsQuery.isLoading}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                    >
                      <option value="" disabled>
                        {locationsQuery.isLoading
                          ? t('documents.upload.departmentLoading')
                          : t('documents.upload.departmentPlaceholder')}
                      </option>
                      {locations.map((loc) => (
                        <option key={loc.id} value={loc.id}>
                          {loc.name}
                        </option>
                      ))}
                    </select>
                  )}
                  {!locationsQuery.isLoading && !locationsQuery.isError && locations.length === 0 && (
                    <p className="mt-1 text-xs text-amber-600">
                      {t('documents.upload.departmentEmpty')}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-neutral-500">
                    {t('documents.upload.departmentNote')}
                  </p>
                </div>
              )}

              {/* Error message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
                  {error}
                </div>
              )}

              {/* Upload button */}
              <Button
                onClick={handleUpload}
                disabled={!isFormValid || isUploading}
                className="w-full"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" />
                    {t('documents.upload.uploading')}
                  </>
                ) : (
                  t('documents.upload.uploadButton')
                )}
              </Button>
            </div>
          )}
        </Card>
      </main>
    </div>
  );
}
